import { Link, Obj, Widget, load } from "scrivito"

import { LOCALIZABLE_ATTRIBUTES, ScalarAttrType } from "./localizableAttributes"

export type CsvRow = {
  content_id: string
  de_obj_id: string
  en_obj_id: string
  de_widget_id: string
  en_widget_id: string
  object_class: string
  page_path: string
  page_title_de: string
  breadcrumb: string
  attribute: string
  attribute_type: string
  index: string
  de_value: string
  en_value: string
}

type PageContext = { path: string; titleDe: string }

/** Common fields shared by every row emitted for the same DE/EN target. */
type RowTarget = {
  contentId: string
  deObjId: string
  enObjId: string
  deWidgetId: string
  enWidgetId: string
  objClass: string
  pageCtx: PageContext
  breadcrumb: string
}

const PAGE_OBJ_CLASSES = new Set([
  "Homepage",
  "Page",
  "LandingPage",
  "PortalNewsPage",
  "PortalNewsPost",
  "PortfolioPage",
  "BuildingReferencesOverviewPage",
  "KpiPage",
  "FavoritesPage",
  "ProjectsPage",
  "ProjectPage",
  "ProjectTeamInvitationListPage",
  "SharedProjectPage",
  "TeamsOverviewPage",
  "DimConfigurationAdminPage",
  "FollowUpConfigurationAdminPage",
  "ProductCategory",
  "BuildingReference",
  "InstallationReference"
])

function readString(obj: Obj | Widget, attr: string): string {
  const val = obj.get(attr)
  if (val === null || val === undefined) return ""
  return String(val)
}

/** Title of a link attribute, or "" when unset. */
function readLinkTitle(obj: Obj | Widget, attr: string): string {
  const link = obj.get(attr) as Link | null
  return link?.title() ?? ""
}

function baseRow(
  target: RowTarget
): Omit<
  CsvRow,
  "attribute" | "attribute_type" | "index" | "de_value" | "en_value"
> {
  return {
    content_id: target.contentId,
    de_obj_id: target.deObjId,
    en_obj_id: target.enObjId,
    de_widget_id: target.deWidgetId,
    en_widget_id: target.enWidgetId,
    object_class: target.objClass,
    page_path: target.pageCtx.path,
    page_title_de: target.pageCtx.titleDe,
    breadcrumb: target.breadcrumb
  }
}

function emitScalarRows(
  deObj: Obj | Widget,
  enObj: Obj | Widget | null,
  target: RowTarget,
  rows: CsvRow[]
) {
  const attrMap = LOCALIZABLE_ATTRIBUTES[target.objClass]
  if (!attrMap) return

  Object.entries(attrMap)
    .filter(([, spec]) => spec.type !== "widgetlist")
    .forEach(([attr, spec]) => {
      if (spec.type === "stringlist" || spec.type === "linklist") {
        const isLinks = spec.type === "linklist"
        const deList = isLinks
          ? ((deObj.get(attr) as Link[] | null) ?? []).map(l => l.title() ?? "")
          : (deObj.get(attr) as string[] | null) ?? []
        const enList = isLinks
          ? ((enObj?.get(attr) as Link[] | null) ?? []).map(
              l => l.title() ?? ""
            )
          : (enObj?.get(attr) as string[] | null) ?? []

        deList.forEach((deVal, i) => {
          rows.push({
            ...baseRow(target),
            attribute: attr,
            attribute_type: spec.type,
            index: String(i),
            de_value: deVal ?? "",
            en_value: enList[i] ?? ""
          })
        })

        enList.slice(deList.length).forEach((enVal, offset) => {
          rows.push({
            ...baseRow(target),
            attribute: attr,
            attribute_type: spec.type,
            index: String(deList.length + offset),
            de_value: "",
            en_value: enVal ?? ""
          })
        })
      } else {
        const type = spec.type as ScalarAttrType
        const read = (o: Obj | Widget) =>
          type === "link" ? readLinkTitle(o, attr) : readString(o, attr)
        const deVal = read(deObj)
        const enVal = enObj ? read(enObj) : ""
        if (!deVal && !enVal) return
        rows.push({
          ...baseRow(target),
          attribute: attr,
          attribute_type: type,
          index: "",
          de_value: deVal,
          en_value: enVal
        })
      }
    })
}

function traverseWidgets(
  deWidgets: Widget[],
  enWidgets: Widget[],
  attrName: string,
  parentBreadcrumb: string,
  parent: RowTarget,
  rows: CsvRow[]
) {
  deWidgets.forEach((deWidget, i) => {
    const enWidget = enWidgets[i] ?? null
    const objClass = deWidget.objClass()
    const breadcrumb =
      parentBreadcrumb === "(page root)"
        ? `${attrName}[${i}]`
        : `${parentBreadcrumb} > ${attrName}[${i}]`

    const target: RowTarget = {
      ...parent,
      objClass,
      breadcrumb,
      deWidgetId: deWidget.id(),
      enWidgetId: enWidget?.id() ?? ""
    }

    emitScalarRows(deWidget, enWidget, target, rows)

    const attrMap = LOCALIZABLE_ATTRIBUTES[objClass]
    if (!attrMap) return

    Object.entries(attrMap)
      .filter(([, spec]) => spec.type === "widgetlist")
      .forEach(([childAttr]) => {
        const childDeWidgets = (deWidget.get(childAttr) as Widget[]) ?? []
        const childEnWidgets = enWidget
          ? (enWidget.get(childAttr) as Widget[]) ?? []
          : []
        traverseWidgets(
          childDeWidgets,
          childEnWidgets,
          childAttr,
          breadcrumb,
          target,
          rows
        )
      })
  })
}

export async function collectRows(): Promise<CsvRow[]> {
  const allDeObjs = await load(() => Obj.onSite("de").all().toArray())
  const deObjs = allDeObjs.filter(
    obj => LOCALIZABLE_ATTRIBUTES[obj.objClass()] !== undefined
  )

  const enObjs = await Promise.all(
    deObjs.map(deObj => load(() => deObj.versionOnSite("en")))
  )

  const rows: CsvRow[] = []

  deObjs.forEach((deObj, idx) => {
    const enObj = enObjs[idx] ?? null
    const objClass = deObj.objClass()

    const pageCtx: PageContext = PAGE_OBJ_CLASSES.has(objClass)
      ? { path: deObj.path() ?? "", titleDe: readString(deObj, "title") }
      : { path: "", titleDe: "" }

    const objTarget: RowTarget = {
      contentId: deObj.contentId(),
      deObjId: deObj.id(),
      enObjId: enObj?.id() ?? "",
      deWidgetId: "",
      enWidgetId: "",
      objClass,
      pageCtx,
      breadcrumb: "(page root)"
    }

    emitScalarRows(deObj, enObj, objTarget, rows)

    const attrMap = LOCALIZABLE_ATTRIBUTES[objClass]
    Object.entries(attrMap)
      .filter(([, spec]) => spec.type === "widgetlist")
      .forEach(([attr]) => {
        const deWidgets = (deObj.get(attr) as Widget[]) ?? []
        const enWidgets = enObj ? (enObj.get(attr) as Widget[]) ?? [] : []
        traverseWidgets(
          deWidgets,
          enWidgets,
          attr,
          "(page root)",
          objTarget,
          rows
        )
      })
  })

  return rows
}
